import type { BoxProgramSlotDefaultSettingsEntity } from "@/modules/boxProgrammation/entities/boxProgramSlotDefaultSettingsEntity"
import { BOX_SLOT_DURATION_DEFAULT_VALUE, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@/modules/boxSlot/boxSlotTypes"
import type { BoxSlotTemplateRangeFormikType } from "@/modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import type { SlotTemplateRequestDto } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateDto"
import type { UserEntity } from "@/modules/profile/userProfile/userEntity"
import dayjs from "dayjs"

type BoxSlotTemplateGroupFormikState = Omit<BoxSlotTemplateGroupFormikEntity, "toInputDto" | "toEntity">

export class BoxSlotTemplateGroupFormikEntity {
  boxProgramId: string
  participantLimit: number
  slotDuration: number
  limitStart?: number
  limitEnd?: number
  limitCancel?: number
  location?: string
  allowMultiBooking: boolean
  slotTemplatesRanges: BoxSlotTemplateRangeFormikType[]
  assignedStaffMembers: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean

  private constructor(state: BoxSlotTemplateGroupFormikState) {
    this.boxProgramId = state.boxProgramId
    this.participantLimit = state.participantLimit
    this.slotDuration = state.slotDuration
    this.limitStart = state.limitStart
    this.limitEnd = state.limitEnd
    this.limitCancel = state.limitCancel
    this.location = state.location
    this.allowMultiBooking = state.allowMultiBooking
    this.slotTemplatesRanges = state.slotTemplatesRanges
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
  }

  static fromDefaultSettings(
    defaultSettings: BoxProgramSlotDefaultSettingsEntity,
    boxProgramId: string,
  ): BoxSlotTemplateGroupFormikEntity {
    return new BoxSlotTemplateGroupFormikEntity({
      boxProgramId,
      slotDuration: defaultSettings.duration ?? BOX_SLOT_DURATION_DEFAULT_VALUE,
      participantLimit: defaultSettings.participantLimit ?? BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
      slotTemplatesRanges: [],
      location: defaultSettings.location ?? undefined,
      limitStart: defaultSettings.participation?.limitStart ?? undefined,
      limitEnd: defaultSettings.participation?.limitEnd ?? undefined,
      limitCancel: defaultSettings.participation?.limitCancel ?? undefined,
      allowMultiBooking: defaultSettings.allowMultiBooking,
      assignedStaffMembers: defaultSettings.assignedStaffMembers,
      dropinsAccepted: defaultSettings.dropinsAccepted,
      trialsAccepted: defaultSettings.trialsAccepted,
    })
  }

  toInputDto(): SlotTemplateRequestDto[] {
    const slotDuration = this.slotDuration

    if (!slotDuration || slotDuration <= 0) return []

    return this.slotTemplatesRanges.flatMap((range) => {
      const start = dayjs(range.startTime)
      const end = dayjs(range.endTime)

      let current = start
      const slots: SlotTemplateRequestDto[] = []

      while (current.add(slotDuration, "minute").isSameOrBefore(end)) {
        const slotStart = current
        const slotEnd = current.add(slotDuration, "minute")

        slots.push({
          weekDay: range.weekDay,
          startTime: slotStart.format("HH:mm:ss"),
          endTime: slotEnd.format("HH:mm:ss"),
          participantLimit: this.participantLimit,
          participation:
            this.limitStart == null && this.limitEnd == null && this.limitCancel == null
              ? null
              : {
                  limitStart: this.limitStart ?? null,
                  limitEnd: this.limitEnd ?? null,
                  limitCancel: this.limitCancel ?? null,
                },
          location: this.location ?? null,
          allowMultiBooking: this.allowMultiBooking,
          assignedStaffMembers: this.assignedStaffMembers.map((member) => member.id),
          dropinsAccepted: this.dropinsAccepted,
          trialsAccepted: this.trialsAccepted,
          impactAlreadyCreatedSlot: false,
        })

        current = slotEnd
      }

      return slots
    })
  }
}
